import { useMemo, useState, useCallback, useEffect, useContext } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import ReactECharts from 'echarts-for-react';
import { Loader2 } from '../components/icons';
import { ComponenteMapas } from '../components/dashboard/ComponenteMapas';
import DashboardFilters from '../components/dashboard/DashboardFilters';
import { optionsPartido, optionsTipoEntidad } from '../datos/options';
import { AuthContext } from '../context/authContext';

const tooltipOscuro = {
    backgroundColor: '#1e293b',
    borderWidth: 0,
    textStyle: { color: '#ffffff', fontSize: 12 },
    padding: [10, 15],
};

const PALETA = ['#0284c7', '#3b82f6', '#10b981', '#84cc16', '#8cd45c', '#f59e0b', '#64748b', '#94a3b8'];

const SERVICIO_CATEGORIA_LABEL = {
    red_publica: 'Red Pública',
    generador: 'Generador Propio',
    perforacion: 'Perforación / Bomba',
    natural: 'Red de gas natural',
    envasado: 'Gas envasado',
    pozo: 'Pozo ciego / Alt.',
    no_posee: 'No posee',
    si: 'Sí',
    no: 'No',
    otro: 'Otro',
};

const Card = ({ titulo, subtitulo, children, altura = 'h-[380px]', extra = null }) => (
    <div className={`rounded-xl border border-gray-200 bg-white p-6 shadow-sm ${altura}`}>
        <div className="flex items-start justify-between gap-3">
            <div>
                <h3 className="text-base font-bold text-gray-800 tracking-tight uppercase">{titulo}</h3>
                {subtitulo && <p className="text-xs text-gray-400">{subtitulo}</p>}
            </div>
            {extra}
        </div>
        <div className="flex-1 w-full min-h-0 mt-4 h-[calc(100%-3rem)]">
            {children}
        </div>
    </div>
);

const KpiCard = ({ label, value, unit = '' }) => (
    <div className="rounded-xl border border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-700 p-6 shadow-sm text-center flex flex-col justify-center">
        <p className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{label}</p>
        <p className="text-4xl font-bold text-blue-600 mt-2">
            {value.toLocaleString('es-AR')}
            {unit && <span className="text-xl text-gray-400 ml-1">{unit}</span>}
        </p>
    </div>
);

const chartStyle = { height: '100%', width: '100%' };

const FILTRO_A_CATEGORIA = {
    tipoEntidad: (v) => v,
    personeria: (v) => ({ 'Sí': 'Tiene', 'No': 'No tiene', 'En trámite': 'En trámite', 'Sin dato': 'Sin dato' })[v],
    predio: (v) => v,
    servicios: (v) => ({ energia: 'Electricidad', agua: 'Agua Potable', gas: 'Gas', cloacas: 'Cloacas', 'Sin dato': 'Sin dato' })[v],
    adaptativo: (v) => ({ si: 'Sí', no: 'No', 'Sin dato': 'Sin dato' })[v],
};
const CHART_FILTER_CAMPO = {
    tipoEntidad: 'tipoEntidad',
    personeria: 'personeria',
    predio: 'predio',
    servicios: 'servicio',
    adaptativo: 'deporteAdaptado',
};

export const TableroPage = () => {
    const { role, userMunicipio } = useContext(AuthContext);
    const esMunicipio = role === 'editor_municipio';
    const partidoInicial = esMunicipio && userMunicipio ? [userMunicipio] : [];

    const [filtros, setFiltros] = useState({
        partido: partidoInicial,
        region: '',
        seccion: [],
        tipoEntidad: '',
        personeria: '',
        predio: '',
        deporteAdaptado: '',
        servicio: '',
    });

    const [mostrarTop15, setMostrarTop15] = useState(true);

    const [mostrarSinDato, setMostrarSinDato] = useState({
        servicios: false,
        accesibilidad: false,
        disciplinas: false,
        necesidades: false,
        personeria: false,
        genero: false,
    });

    const toggleSinDato = (chartKey) => {
        setMostrarSinDato(prev => ({ ...prev, [chartKey]: !prev[chartKey] }));
    };

    const CheckSinDato = ({ chartKey }) => (
        <label className="flex items-center space-x-2 text-xs text-gray-600 cursor-pointer bg-gray-50 hover:bg-gray-100 px-2.5 py-1.5 rounded-md border border-gray-200 transition-colors select-none">
            <input
                type="checkbox"
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                checked={mostrarSinDato[chartKey]}
                onChange={() => toggleSinDato(chartKey)}
            />
            <span className="font-medium text-gray-700">Mostrar Sin dato</span>
        </label>
    );

    useEffect(() => {
        setMostrarSinDato(prev => {
            const nuevo = filtros.personeria === 'Sin dato';
            return prev.personeria === nuevo ? prev : { ...prev, personeria: nuevo };
        });
    }, [filtros.personeria]);

    const [seleccionChart, setSeleccionChart] = useState({});
    const clickChart = (chartKey, obtenerNombre = (params) => params?.name) => (params) => {
        const nombre = obtenerNombre(params);
        if (!nombre) return;
        setSeleccionChart(prev => ({ ...prev, [chartKey]: prev[chartKey] === nombre ? null : nombre }));
    };
    const onEventsChart = (chartKey, obtenerNombre) => ({ click: clickChart(chartKey, obtenerNombre) });

    const dimItem = useCallback((chartKey, nombre) => {
        const sel = seleccionChart[chartKey];
        if (sel) return sel !== nombre;
        const campo = CHART_FILTER_CAMPO[chartKey];
        const valor = campo ? filtros[campo] : '';
        if (valor) {
            const categoria = FILTRO_A_CATEGORIA[chartKey](valor);
            return Boolean(categoria) && categoria !== nombre;
        }
        return false;
    }, [seleccionChart, filtros]);

    const dimSerie = useCallback((chartKey, nombre, esSinDato) => {
        const sel = seleccionChart[chartKey];
        const campo = CHART_FILTER_CAMPO[chartKey];
        const valor = campo ? filtros[campo] : '';
        if (sel) {
            if (sel === 'Sin dato') return !esSinDato;
            return esSinDato ? true : sel !== nombre;
        }
        if (valor) {
            const categoria = FILTRO_A_CATEGORIA[chartKey](valor);
            if (categoria === 'Sin dato') return !esSinDato;
            return esSinDato ? true : categoria !== nombre;
        }
        return false;
    }, [seleccionChart, filtros]);

    const handleFilterChange = (e) => {
        setFiltros((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const resetFiltros = () => {
        setFiltros({
            partido: partidoInicial,
            region: '', seccion: [], tipoEntidad: '',
            personeria: '', predio: '', deporteAdaptado: '', servicio: '',
        });
    };

    const queryParams = useMemo(() => {
        const p = new URLSearchParams();
        Object.entries(filtros).forEach(([k, v]) => {
            if (Array.isArray(v)) {
                v.forEach(item => { if (item && item !== '') p.append(k, item); });
            } else if (v && v !== '') {
                p.set(k, v);
            }
        });
        const s = p.toString();
        return s ? `?${s}` : '';
    }, [filtros]);

    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: ['dashboard-entidades', queryParams],
        queryFn: async () => {
            const rawToken = localStorage.getItem('token');
            const token = rawToken ? JSON.parse(rawToken) : null;
            const res = await fetch(`/api/entidades/dashboard${queryParams}`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {},
            });
            if (!res.ok) throw new Error('Error al obtener datos del tablero');
            return res.json();
        },
        placeholderData: keepPreviousData,
    });

    const totalEntidades = data?.totalEntidades ?? 0;

    const personasVigentes = data?.kpis?.personasVigentes ?? 0;

    const predioPropio = data?.kpis?.predioPropio ?? 0;

    const partidosSeleccionados = useMemo(
        () => (filtros.partido || []).filter(v => v && v !== 'Todos'),
        [filtros.partido]
    );

    const alcancePartidos = useMemo(() => {
        if (partidosSeleccionados.length > 0) {
            return partidosSeleccionados;
        }
        if (filtros.seccion && filtros.seccion.length > 0) {
            return optionsPartido
                .filter(p => filtros.seccion.includes(p.seccion_electoral))
                .map(p => p.value);
        }
        return optionsPartido.map(p => p.value);
    }, [partidosSeleccionados, filtros.seccion]);

    const partidosAlcanzados = useMemo(() => {
        const conEntidades = new Set((data?.entidadesPorPartido || []).map(p => String(p.codigo_partido)));
        return alcancePartidos.filter(v => conEntidades.has(String(v))).length;
    }, [data, alcancePartidos]);

    const optionPartidosAlcanzados = useMemo(() => ({
        series: [{
            type: 'gauge',
            max: 135,
            startAngle: 210,
            endAngle: -30,
            progress: {
                show: true,
                width: 14,
                itemStyle: { color: '#3b82f6' },
            },
            axisLine: {
                lineStyle: { width: 14, color: [[1, '#e5e7eb']] },
            },
            pointer: { show: false },
            axisTick: { show: false },
            splitLine: { show: false },
            axisLabel: {
                show: true,
                distance: -20,
                formatter: (value) => (value === 0 || value === 135) ? value : '',
                color: '#9ca3af',
                fontSize: 12,
            },
            detail: {
                valueAnimation: true,
                formatter: '{value}',
                offsetCenter: [0, '10%'],
                textStyle: {
                    fontSize: 42,
                    fontWeight: 'bold',
                    color: '#1f2937',
                },
            },
            data: [{ value: partidosAlcanzados }],
        }],
    }), [partidosAlcanzados]);

    const optionTipoEntidad = useMemo(() => ({
        tooltip: {
            ...tooltipOscuro,
            trigger: 'item',
            formatter: (params) => {
                const label = optionsTipoEntidad.find(o => o.value === params.name)?.label || params.name;
                return `${label}: ${params.value} (${params.percent}%)`;
            },
        },
        legend: {
            type: 'scroll',
            orient: 'vertical',
            right: '5%',
            top: 'center',
            itemGap: 12,
            textStyle: { color: '#4b5563', fontSize: 12 },
            formatter: function (name) {
                const encontrado = (data?.tipoEntidad || []).find(t => t.name === name);
                const cantidad = encontrado ? encontrado.value : 0;
                const label = optionsTipoEntidad.find(o => o.value === name)?.label || name;
                const nombreCorto = label.length > 30 ? label.substring(0, 27) + '...' : label;
                return `${nombreCorto} (${cantidad})`;
            },
        },
        series: [{
            type: 'pie',
            radius: ['40%', '70%'],
            center: ['30%', '50%'],
            minAngle: 12,
            avoidLabelOverlap: true,
            itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
            label: { show: false, position: 'center' },
            emphasis: {
                label: {
                    show: true,
                    fontSize: 16,
                    fontWeight: 'bold',
                    formatter: '{b}',
                },
            },
            labelLine: { show: false },
            data: (data?.tipoEntidad || []).map((t, i) => ({
                name: t.name,
                value: t.value,
                itemStyle: {
                    color: t.name === 'Sin dato' ? '#cbd5e1' : (PALETA[i % PALETA.length]),
                    opacity: dimItem('tipoEntidad', t.name) ? 0.25 : 1,
                },
            })),
        }],
    }), [data, dimItem]);

    const optionEntidadesPartido = useMemo(() => {
        const todos = data?.entidadesPorPartido || [];
        const sinFiltro = filtros.partido.length === 0 || filtros.partido.includes('Todos');
        const seleccionados = new Set(filtros.partido);
        const limite = mostrarTop15 ? 15 : 30;
        const partidos = sinFiltro
            ? todos.slice(0, limite)
            : [
                  ...todos.filter(p => seleccionados.has(p.codigo_partido)),
                  ...todos.filter(p => !seleccionados.has(p.codigo_partido)).slice(0, Math.max(0, limite - Math.min(seleccionados.size, todos.length))),
              ];
        const esSeleccionado = (codigoPartido) =>
            sinFiltro || seleccionados.has(codigoPartido);
        const barras = partidos.map(p => ({
            value: parseInt(p.cantidad || 0),
            itemStyle: {
                color: esSeleccionado(p.codigo_partido) ? '#84cc16' : '#cbd5e1',
                borderRadius: [0, 4, 4, 0],
            },
        })).reverse();
        const coloresLabel = partidos.map(p => esSeleccionado(p.codigo_partido) ? '#374151' : '#9ca3af').reverse();
        return {
            tooltip: { ...tooltipOscuro, trigger: 'axis', axisPointer: { type: 'shadow' }, formatter: '{b}: <b>{c} entidades</b>' },
            grid: { left: '3%', right: '8%', bottom: '3%', top: '3%', containLabel: true },
            xAxis: {
                type: 'value',
                max: partidos.length > 0
                    ? Math.max(...partidos.map(p => parseInt(p.cantidad || 0)))
                    : 'dataMax',
                splitLine: { lineStyle: { type: 'dashed', color: '#e5e7eb' } },
                axisLabel: { color: '#64748b' },
            },
            yAxis: {
                type: 'category',
                data: partidos.map(p => p.nombre).reverse(),
                axisLabel: { fontSize: 11, color: '#374151' },
                axisLine: { lineStyle: { color: '#d1d5db' } },
            },
            dataZoom: !mostrarTop15 ? [
                {
                    type: 'inside',
                    yAxisIndex: 0,
                    start: 0,
                    end: 60,
                    zoomOnMouseWheel: false,
                    moveOnMouseWheel: true,
                    moveOnMouseMove: true,
                    preventDefaultMouseMove: true,
                },
                {
                    type: 'slider',
                    yAxisIndex: 0,
                    right: 10,
                    width: 15,
                    start: 0,
                    end: 60,
                    textStyle: { show: false },
                },
            ] : [],
            series: [{
                name: 'Entidades',
                type: 'bar',
                data: barras,
                label: {
                    show: true,
                    position: 'right',
                    color: (params) => coloresLabel[params.dataIndex],
                    fontWeight: 'bold',
                    fontSize: 11,
                },
                barMaxWidth: 22,
            }],
        };
    }, [data, mostrarTop15, filtros.partido]);

    const optionPersoneria = useMemo(() => {
        const items = data?.personeriaJuridica || [];
        const conDato = items.filter(p => p.categoria !== 'Sin dato');
        const categorias = mostrarSinDato.personeria
            ? items.map(p => p.categoria)
            : conDato.map(p => p.categoria);
        const pSin = items.find(x => x.categoria === 'Sin dato');
        return {
            tooltip: {
                ...tooltipOscuro,
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params) => {
                    const validos = (params || []).filter(p => p.value > 0);
                    if (!validos.length) return '';
                    const nombre = validos[0].axisValue || validos[0].name;
                    return `<div style="font-weight:bold;margin-bottom:4px">${nombre}</div>` +
                        validos.map(p => `<div>${p.marker}${p.seriesName}: <b>${p.value}</b></div>`).join('');
                },
            },
            legend: { bottom: 0, icon: 'circle', itemWidth: 8, textStyle: { color: '#64748b' } },
            grid: { left: '8%', right: '8%', top: 20, bottom: 40 },
            xAxis: { type: 'category', data: categorias, axisLabel: { color: '#64748b' } },
            yAxis: { type: 'value', splitLine: { lineStyle: { type: 'dashed', color: '#e5e7eb' } }, axisLabel: { color: '#64748b' } },
            series: [{
                name: 'Entidades', type: 'bar',
                data: categorias.map(cat => {
                    const esSin = cat === 'Sin dato';
                    const p = esSin ? pSin : conDato.find(x => x.categoria === cat);
                    const dim = mostrarSinDato.personeria
                        ? !esSin
                        : dimItem('personeria', cat);
                    return {
                        value: p ? p.value : 0,
                        itemStyle: {
                            color: esSin ? '#cbd5e1' : '#3b82f6',
                            opacity: dim ? 0.25 : 1,
                            borderRadius: [4, 4, 0, 0],
                        },
                    };
                }),
                barMaxWidth: 56,
                label: { show: true, position: 'top', color: '#475569', fontWeight: 'bold' },
            }],
        };
    }, [data, dimItem, mostrarSinDato.personeria]);

    const optionPredio = useMemo(() => ({
        tooltip: { ...tooltipOscuro, trigger: 'item', formatter: '{b}: {c} ({d}%)' },
        legend: {
            type: 'scroll',
            orient: 'vertical',
            right: '5%',
            top: 'center',
            itemGap: 12,
            textStyle: { color: '#4b5563', fontSize: 12 },
            formatter: function (name) {
                const encontrado = (data?.predioPropio || []).find(p => p.category === name);
                const cantidad = encontrado ? encontrado.value : 0;
                const nombreCorto = name.length > 30 ? name.substring(0, 27) + '...' : name;
                return `${nombreCorto} (${cantidad})`;
            },
        },
        series: [{
            type: 'pie',
            radius: ['45%', '70%'],
            center: ['30%', '50%'],
            minAngle: 12,
            itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
            label: { show: false, position: 'center' },
            emphasis: {
                label: {
                    show: true,
                    fontSize: 16,
                    fontWeight: 'bold',
                    formatter: '{b}',
                },
            },
            labelLine: { show: false },
            data: (data?.predioPropio || []).map((p) => ({
                name: p.category,
                value: p.value,
                itemStyle: {
                    color: p.category === 'Sí' ? '#10b981' : p.category === 'No' ? '#ef4444' : '#cbd5e1',
                    opacity: dimItem('predio', p.category) ? 0.25 : 1,
                },
            })),
        }],
    }), [data, dimItem]);

const optionServicios = useMemo(() => {
        const items = data?.servicios || [];
        return {
            tooltip: {
                ...tooltipOscuro,
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params) => {
                    const item = params[0];
                    if (!item) return '';
                    const serv = items.find(s => s.servicio === item.name);
                    if (!serv) return `<b>${item.name}</b>`;
                    let html = `<div style="font-weight:bold;margin-bottom:4px">${serv.servicio}</div>`;
                    if (serv.categorias && serv.categorias.length > 0) {
                        html += serv.categorias.map(c =>
                            `<div>${SERVICIO_CATEGORIA_LABEL[c.valor] || c.valor}: <b>${c.cantidad}</b></div>`
                        ).join('');
                        html += `<div style="border-top:1px solid #475569;margin-top:4px;padding-top:4px">Total: <b>${serv.cantidad}</b></div>`;
                    } else {
                        html += `<div>Total: <b>${serv.cantidad}</b></div>`;
                    }
                    return html;
                },
            },
            legend: {
                bottom: 0,
                icon: 'circle',
                itemWidth: 8,
                textStyle: { color: '#64748b' },
                data: [
                    { name: 'Con dato', itemStyle: { color: '#0284c7' } },
                    { name: 'No posee', itemStyle: { color: '#10b981' } },
                    { name: 'Sin dato', itemStyle: { color: '#cbd5e1' } },
                ],
            },
            grid: { left: '8%', right: '8%', top: 20, bottom: 40 },
            xAxis: { type: 'category', data: items.map(s => s.servicio), axisLabel: { color: '#64748b' } },
            yAxis: { type: 'value', splitLine: { lineStyle: { type: 'dashed', color: '#e5e7eb' } }, axisLabel: { color: '#64748b' } },
            series: [
                {
                    name: 'Con dato', type: 'bar',
                    data: items.map(s => ({
                        value: s.cantidad,
                        itemStyle: {
                            color: '#0284c7',
                            opacity: dimSerie('servicios', s.servicio, false) ? 0.25 : 1,
                            borderRadius: [4, 4, 0, 0],
                        },
                    })),
                    barMaxWidth: 28,
                    label: { show: true, position: 'top', color: '#0369a1', fontWeight: 'bold' },
                },
                {
                    name: 'No posee', type: 'bar',
                    data: items.map(s => ({
                        value: s.noPosee || 0,
                        itemStyle: {
                            color: '#10b981',
                            opacity: dimSerie('servicios', s.servicio, false) ? 0.25 : 1,
                            borderRadius: [4, 4, 0, 0],
                        },
                    })),
                    barMaxWidth: 28,
                    label: { show: true, position: 'top', color: '#047857', fontWeight: 'bold' },
                },
                ...(mostrarSinDato.servicios ? [{
                    name: 'Sin dato', type: 'bar',
                    data: items.map(s => ({
                        value: s.sin,
                        itemStyle: {
                            color: '#cbd5e1',
                            opacity: dimSerie('servicios', s.servicio, true) ? 0.25 : 1,
                            borderRadius: [4, 4, 0, 0],
                        },
                    })),
                    barMaxWidth: 28,
                    label: { show: true, position: 'top', color: '#94a3b8', fontWeight: 'bold' },
                }] : []),
            ],
        };
    }, [data, dimSerie, mostrarSinDato.servicios]);

    const optionAccesibilidad = useMemo(() => {
        const items = data?.accesibilidad || [];
        return {
            tooltip: { ...tooltipOscuro, trigger: 'axis', axisPointer: { type: 'shadow' } },
            legend: { bottom: 0, icon: 'circle', itemWidth: 8, textStyle: { color: '#64748b' } },
            grid: { left: '8%', right: '8%', top: 20, bottom: 40 },
            xAxis: { type: 'category', data: items.map(a => a.item), axisLabel: { color: '#64748b' } },
            yAxis: { type: 'value', splitLine: { lineStyle: { type: 'dashed', color: '#e5e7eb' } }, axisLabel: { color: '#64748b' } },
            series: [
                {
                    name: 'Con dato', type: 'bar',
                    data: items.map(a => ({
                        value: a.cantidad,
                        itemStyle: {
                            color: '#3b82f6',
                            opacity: dimSerie('accesibilidad', a.item, false) ? 0.25 : 1,
                            borderRadius: [4, 4, 0, 0],
                        },
                    })),
                    barMaxWidth: 28,
                    label: { show: true, position: 'top', color: '#475569', fontWeight: 'bold' },
                },
                ...(mostrarSinDato.accesibilidad ? [{
                    name: 'Sin dato', type: 'bar',
                    data: items.map(a => ({
                        value: a.sin,
                        itemStyle: {
                            color: '#cbd5e1',
                            opacity: dimSerie('accesibilidad', a.item, true) ? 0.25 : 1,
                            borderRadius: [4, 4, 0, 0],
                        },
                    })),
                    barMaxWidth: 28,
                    label: { show: true, position: 'top', color: '#94a3b8', fontWeight: 'bold' },
                }] : []),
            ],
        };
    }, [data, dimSerie, mostrarSinDato.accesibilidad]);

    const optionDisciplinas = useMemo(() => {
        const disciplinas = (data?.disciplinas || []).slice(0, 10);
        return {
            tooltip: {
                ...tooltipOscuro,
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params) => {
                    const item = params[0];
                    if (!item) return '';
                    const disc = disciplinas.find(d => d.nombre === item.name);
                    if (!disc) return `<b>${item.name}</b>`;
                    let html = `<div style="font-weight:bold;margin-bottom:4px">${item.name}</div>`;
                    if (disc.categorias && disc.categorias.length > 0) {
                        html += disc.categorias.map(c =>
                            `<div>${c.categoria}: <b>${c.cantidad}</b></div>`
                        ).join('');
                        html += `<div style="border-top:1px solid #475569;margin-top:4px;padding-top:4px">Entidades con la disciplina: <b>${disc.total}</b></div>`;
                    } else {
                        html += `<div>Entidades con la disciplina: <b>${disc.total}</b></div>`;
                    }
                    return html;
                },
            },
            legend: { bottom: 0, icon: 'circle', itemWidth: 8, textStyle: { color: '#64748b' } },
            grid: { left: '8%', right: '8%', top: 20, bottom: 40 },
            xAxis: { type: 'value', splitLine: { lineStyle: { type: 'dashed', color: '#e5e7eb' } }, axisLabel: { color: '#64748b' } },
            yAxis: { type: 'category', data: disciplinas.map(d => d.nombre).reverse(), axisLabel: { color: '#334155', width: 90, overflow: 'truncate' } },
            series: [
                {
                    name: 'Con dato', type: 'bar',
                    data: disciplinas.map(d => ({
                        value: d.total,
                        itemStyle: {
                            color: '#3b82f6',
                            opacity: dimSerie('disciplinas', d.nombre, false) ? 0.25 : 1,
                            borderRadius: [0, 4, 4, 0],
                        },
                    })).reverse(),
                    barMaxWidth: 16,
                    label: { show: true, position: 'right', color: '#475569', fontWeight: 'bold' },
                },
                ...(mostrarSinDato.disciplinas ? [{
                    name: 'Sin dato', type: 'bar',
                    data: disciplinas.map(d => ({
                        value: d.sin,
                        itemStyle: {
                            color: '#cbd5e1',
                            opacity: dimSerie('disciplinas', d.nombre, true) ? 0.25 : 1,
                            borderRadius: [0, 4, 4, 0],
                        },
                    })).reverse(),
                    barMaxWidth: 16,
                    label: { show: true, position: 'right', color: '#94a3b8', fontWeight: 'bold' },
                }] : []),
            ],
        };
    }, [data, dimSerie, mostrarSinDato.disciplinas]);

    const optionNecesidades = useMemo(() => {
        const necesidades = (data?.necesidades || []).slice(0, 10);
        return {
            tooltip: { ...tooltipOscuro, trigger: 'axis', axisPointer: { type: 'shadow' } },
            legend: { bottom: 0, icon: 'circle', itemWidth: 8, textStyle: { color: '#64748b' } },
            grid: { left: '8%', right: '8%', top: 20, bottom: 40 },
            xAxis: { type: 'value', splitLine: { lineStyle: { type: 'dashed', color: '#e5e7eb' } }, axisLabel: { color: '#64748b' } },
            yAxis: { type: 'category', data: necesidades.map(n => n.concepto).reverse(), axisLabel: { color: '#334155', width: 110, overflow: 'truncate' } },
            series: [
                {
                    name: 'Con dato', type: 'bar',
                    data: necesidades.map(n => ({
                        value: n.total,
                        itemStyle: {
                            color: '#3b82f6',
                            opacity: dimSerie('necesidades', n.concepto, false) ? 0.25 : 1,
                            borderRadius: [0, 4, 4, 0],
                        },
                    })).reverse(),
                    barMaxWidth: 16,
                    label: { show: true, position: 'right', color: '#475569', fontWeight: 'bold' },
                },
                ...(mostrarSinDato.necesidades ? [{
                    name: 'Sin dato', type: 'bar',
                    data: necesidades.map(n => ({
                        value: n.sin,
                        itemStyle: {
                            color: '#cbd5e1',
                            opacity: dimSerie('necesidades', n.concepto, true) ? 0.25 : 1,
                            borderRadius: [0, 4, 4, 0],
                        },
                    })).reverse(),
                    barMaxWidth: 16,
                    label: { show: true, position: 'right', color: '#94a3b8', fontWeight: 'bold' },
                }] : []),
            ],
        };
    }, [data, dimSerie, mostrarSinDato.necesidades]);

    const optionAdaptativo = useMemo(() => ({
        tooltip: { ...tooltipOscuro, trigger: 'item', formatter: '{b}: {c} ({d}%)' },
        legend: {
            type: 'scroll',
            orient: 'vertical',
            right: '5%',
            top: 'center',
            itemGap: 12,
            textStyle: { color: '#4b5563', fontSize: 12 },
            formatter: function (name) {
                const encontrado = (data?.deporteAdaptativo || []).find(p => p.status === name);
                const cantidad = encontrado ? encontrado.value : 0;
                const nombreCorto = name.length > 30 ? name.substring(0, 27) + '...' : name;
                return `${nombreCorto} (${cantidad})`;
            },
        },
        series: [{
            type: 'pie',
            radius: ['45%', '70%'],
            center: ['30%', '50%'],
            minAngle: 12,
            itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
            label: { show: false, position: 'center' },
            emphasis: {
                label: {
                    show: true,
                    fontSize: 16,
                    fontWeight: 'bold',
                    formatter: '{b}',
                },
            },
            labelLine: { show: false },
            data: (data?.deporteAdaptativo || []).map((p, i) => ({
                name: p.status,
                value: p.value,
                itemStyle: {
                    color: p.status === 'Sin dato' ? '#cbd5e1' : (['#0284c7', '#94a3b8'][i] || PALETA[i]),
                    opacity: dimItem('adaptativo', p.status) ? 0.25 : 1,
                },
            })),
        }],
    }), [data, dimItem]);

    const optionGenero = useMemo(() => ({
        tooltip: { ...tooltipOscuro, trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: {
            bottom: 0,
            icon: 'circle',
            itemWidth: 8,
            textStyle: { color: '#64748b' },
            data: [
                { name: 'Sí', itemStyle: { color: '#0284c7' } },
                { name: 'Sin dato', itemStyle: { color: '#cbd5e1' } },
            ],
        },
        grid: { left: '8%', right: '8%', top: 20, bottom: 40 },
        xAxis: { type: 'category', data: (data?.perspectivaGenero || []).map(g => g.indicador), axisLabel: { color: '#334155', interval: 0, rotate: 20, fontSize: 10 } },
        yAxis: { type: 'value', splitLine: { lineStyle: { type: 'dashed', color: '#e5e7eb' } }, axisLabel: { color: '#64748b' } },
        series: [
            {
                name: 'Sí', type: 'bar',
                data: (data?.perspectivaGenero || []).map(g => g.si),
                itemStyle: { color: '#0284c7', borderRadius: [4, 4, 0, 0], opacity: dimItem('genero', 'Sí') ? 0.25 : 1 },
                barMaxWidth: 28,
                label: { show: true, position: 'top', color: '#0369a1', fontWeight: 'bold' },
            },
            ...(mostrarSinDato.genero ? [{
                name: 'Sin dato', type: 'bar',
                data: (data?.perspectivaGenero || []).map(g => g.sin),
                itemStyle: { color: '#cbd5e1', borderRadius: [4, 4, 0, 0], opacity: dimItem('genero', 'Sin dato') ? 0.25 : 1 },
                barMaxWidth: 28,
                label: { show: true, position: 'top', color: '#94a3b8', fontWeight: 'bold' },
            }] : []),
        ],
    }), [data, dimItem, mostrarSinDato.genero]);

    const optionAsistencia = useMemo(() => ({
        tooltip: { ...tooltipOscuro, trigger: 'item', formatter: '{b}: {c} ({d}%)' },
        legend: {
            type: 'scroll',
            orient: 'vertical',
            right: '5%',
            top: 'center',
            itemGap: 12,
            textStyle: { color: '#4b5563', fontSize: 12 },
            formatter: function (name) {
                const encontrado = (data?.asistenciaEstatal || []).find(a => a.tipo === name);
                const cantidad = encontrado ? encontrado.value : 0;
                const nombreCorto = name.length > 30 ? name.substring(0, 27) + '...' : name;
                return `${nombreCorto} (${cantidad})`;
            },
        },
        series: [{
            type: 'pie',
            radius: ['40%', '70%'],
            center: ['30%', '50%'],
            minAngle: 12,
            itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
            label: { show: false, position: 'center' },
            emphasis: {
                label: {
                    show: true,
                    fontSize: 16,
                    fontWeight: 'bold',
                    formatter: '{b}',
                },
            },
            labelLine: { show: false },
            data: (data?.asistenciaEstatal || []).map((a, i) => ({
                name: a.tipo,
                value: a.value,
                itemStyle: {
                    color: ['#10b981', '#0284c7', '#f59e0b', '#94a3b8'][i] || PALETA[i],
                    opacity: dimItem('asistencia', a.tipo) ? 0.25 : 1,
                },
            })),
        }],
    }), [data, dimItem]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh] text-gray-500">
                <Loader2 className="mr-2 h-6 w-6 animate-spin" />
                Cargando tablero...
            </div>
        );
    }

    if (isError) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
                <p className="text-red-500">Error al cargar los datos del tablero</p>
                <button onClick={() => refetch()} className="px-4 py-2 bg-[#476c97] text-white rounded-md">
                    Reintentar
                </button>
            </div>
        );
    }

    return (
        <div className="w-full p-6 pb-10 bg-gray-50 dark:bg-gray-900 min-h-full">
            <div className="mb-6">
                <div className="flex items-center gap-3 flex-wrap">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                        Tablero de Control
                    </h1>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 border border-amber-300 px-3 py-1 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:border-amber-700 dark:text-amber-300">
                        <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        En desarrollo
                    </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    Registro Provincial de Entidades Deportivas
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                    Este tablero se encuentra en etapa de desarrollo: la información puede cambiar.
                </p>
                <div className="mt-4">
                    <DashboardFilters
                        filtros={filtros}
                        onFilterChange={handleFilterChange}
                        onReset={resetFiltros}
                        loading={isLoading}
                        esMunicipio={esMunicipio}
                        userMunicipio={userMunicipio}
                    />
                </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-6">
                <KpiCard label="Total Entidades" value={totalEntidades} />
                <KpiCard label="Con Personería Vigente" value={personasVigentes} />
                <KpiCard label="Con Predio Propio" value={predioPropio} />
                <div className="rounded-xl border border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-700 p-6 shadow-sm flex flex-col items-center justify-center">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">Partidos Alcanzados</h3>
                    {totalEntidades === 0 ? (
                        <div className="h-40 w-full flex flex-col items-center justify-center gap-1">
                            <svg className="h-6 w-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <p className="text-sm font-semibold text-gray-600 dark:text-gray-300">Sin Partidos Alcanzados</p>
                            <p className="text-xs text-gray-400">La combinación de filtros no devuelve resultados.</p>
                        </div>
                    ) : (
                        <div className="h-40 w-full">
                            <ReactECharts option={optionPartidosAlcanzados} notMerge={true} style={{ height: '100%', width: '100%' }} />
                        </div>
                    )}
                </div>
            </div>

            {/* Mapas de distribución territorial */}
            <ComponenteMapas
                entidadesPorPartido={data?.entidadesPorPartido || []}
                partidoSeleccionadoId={filtros.partido.length > 0 ? filtros.partido : null}
            />

            {/* Entidades por Partido (Top 15) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="flex-1 bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col h-[450px]">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-base font-bold text-gray-800 tracking-tight uppercase">
                            {mostrarTop15 ? "Entidades por Partido (Top 15)" : "Entidades por Partido (Todos)"}
                        </h3>
                        <label className="flex items-center space-x-2 text-sm text-gray-600 cursor-pointer bg-gray-50 hover:bg-gray-100 px-3 py-1.5 rounded-md border border-gray-200 transition-colors select-none">
                            <input
                                type="checkbox"
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                checked={mostrarTop15}
                                onChange={(e) => setMostrarTop15(e.target.checked)}
                            />
                            <span className="font-medium text-gray-700">Limitar a Top 15</span>
                        </label>
                    </div>
                    <div className="flex-1 w-full h-[340px]">
                        <ReactECharts option={optionEntidadesPartido} notMerge={true} style={chartStyle} />
                    </div>
                </div>
                <Card titulo="Tipo de Entidad" subtitulo="Distribución por tipo" altura="h-[450px]">
                    <ReactECharts option={optionTipoEntidad} notMerge={true} style={chartStyle} onEvents={onEventsChart('tipoEntidad')} />
                </Card>
            </div>

            {/* Fila 1 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <Card titulo="Personería Jurídica" subtitulo="Estado de la personería" altura="h-[450px]" extra={<CheckSinDato chartKey="personeria" />}>
                    <ReactECharts option={optionPersoneria} notMerge={true} style={chartStyle} onEvents={onEventsChart('personeria')} />
                </Card>
                <Card titulo="Predio Propio" subtitulo="Situación dominial" altura="h-[450px]">
                    <ReactECharts option={optionPredio} notMerge={true} style={chartStyle} onEvents={onEventsChart('predio')} />
                </Card>
            </div>

            {/* Fila 2 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <Card titulo="Servicios Básicos" subtitulo="Entidades que cuentan con cada servicio" extra={<CheckSinDato chartKey="servicios" />}>
                    <ReactECharts option={optionServicios} notMerge={true} style={chartStyle} onEvents={onEventsChart('servicios')} />
                </Card>
                <Card titulo="Accesibilidad" subtitulo="Entidades con adecuaciones accesibles" extra={<CheckSinDato chartKey="accesibilidad" />}>
                    <ReactECharts option={optionAccesibilidad} notMerge={true} style={chartStyle} onEvents={onEventsChart('accesibilidad')} />
                </Card>
            </div>

            {/* Fila 3 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <Card titulo="Necesidades" subtitulo="Principales necesidades relevadas" altura="h-[450px]" extra={<CheckSinDato chartKey="necesidades" />}>
                    <ReactECharts option={optionNecesidades} notMerge={true} style={chartStyle} onEvents={onEventsChart('necesidades')} />
                </Card>
                <Card titulo="Disciplinas" subtitulo="Practicantes por disciplina (top 10)" altura="h-[450px]" extra={<CheckSinDato chartKey="disciplinas" />}>
                    <ReactECharts option={optionDisciplinas} notMerge={true} style={chartStyle} onEvents={onEventsChart('disciplinas')} />
                </Card>
            </div>

            {/* Fila 4 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <Card titulo="Deporte Adaptado" subtitulo="Entidades que lo desarrollan">
                    <ReactECharts option={optionAdaptativo} notMerge={true} style={chartStyle} onEvents={onEventsChart('adaptativo')} />
                </Card>
                <Card titulo="Asistencia Estatal" subtitulo="Apoyos recibidos por nivel">
                    <ReactECharts option={optionAsistencia} notMerge={true} style={chartStyle} onEvents={onEventsChart('asistencia')} />
                </Card>
            </div>

            {/* Fila 5 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <Card titulo="Perspectiva de Género" subtitulo="Indicadores por entidad" extra={<CheckSinDato chartKey="genero" />}>
                    <ReactECharts option={optionGenero} notMerge={true} style={chartStyle} onEvents={onEventsChart('genero', (params) => params?.seriesName)} />
                </Card>
                <div></div>
            </div>
        </div>
    );
};
